import { useLocation, useHistory } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import UILink from 'UIComponents/link/UILink';
import getLangLocale from 'I18n/utils/getLangLocale';
import { getDashboardRedirectUrl, getDeveloperHomeRedirectUrl, getEnglishBaseUrl, getPublicRootUrl } from '../utils/urls';
import { useAppSelector } from '../reducers';
import { selectIsCRMDevUngated, selectIsPublicDocs } from '../selectors/auth';
import LayoutPublic from './LayoutPublic';
import { Paths } from './RoutesPublic';
import { MAX_WIDTH } from './apiReference/constants';
import { useEffect } from 'react';
import { getUsageTracker } from '../utils/usage-tracker';
import { getLoginUrl } from '../utils/urls';
import getIsPrerenderUserAgent from 'developer-docs-shared-components/utils/getIsPrerenderUserAgent';
import { LinkWithEnglishSuperset } from 'developer-docs-shared-components/components/Link';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Flex } from 'foundations-components/components/Flex';
import useServerSideRedirect from '../hooks/useServerSideRedirect';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NotFound = ({
  protectedDoc
}) => {
  const isPublicDocs = useAppSelector(selectIsPublicDocs);
  const locale = getLangLocale();
  const {
    pathname
  } = useLocation();
  const history = useHistory();
  const isEnUs = locale === 'en-us';
  const {
    loading
  } = useServerSideRedirect({
    locale,
    isPublicDocs,
    pathname
  });
  useEffect(() => {
    if (!loading) {
      getUsageTracker().track('pageView', {
        screen: protectedDoc ? 'forbidden' : 'notFound',
        subscreen: protectedDoc ? 'forbidden' : 'notFound'
      });
    }
    if (isPublicDocs && getIsPrerenderUserAgent()) {
      // Set Prerender status code meta tag @see https://docs.prerender.io/docs/11-best-practices
      const statusCodeEl = document.createElement('meta');
      statusCodeEl.name = 'prerender-status-code';
      statusCodeEl.content = '404';
      document.head.appendChild(statusCodeEl);
    }

    // This is used by Prerender.io across **all** user agents to know when the page is ready to be rendered
    window.prerenderReady = true;
  }, [isPublicDocs, loading, pathname, locale, history, protectedDoc]);

  // If a user is CRM Dev ungated, assume we can redirect to the dev home
  const isCRMDevUngated = useAppSelector(selectIsCRMDevUngated);
  let redirectLink = /*#__PURE__*/_jsx(UILink, {
    href: getDashboardRedirectUrl(),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "developer-docs-ui.gated.dashboard"
    })
  });
  if (isCRMDevUngated) {
    redirectLink = /*#__PURE__*/_jsx(UILink, {
      href: getDeveloperHomeRedirectUrl(),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.notFound.developerHome"
      })
    });
  }
  if (isPublicDocs) {
    redirectLink = /*#__PURE__*/_jsxs(_Fragment, {
      children: [!isEnUs && /*#__PURE__*/_jsx("p", {
        "data-test-id": "non-english-notfound-link",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "developer-docs-ui.notFound.englishVersionDescription_jsx",
          elements: {
            Link: LinkWithEnglishSuperset
          },
          options: {
            href: getEnglishBaseUrl(pathname),
            hrefLang: 'en-us',
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.notFound.englishVersionLinkText"
            })
          }
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(UILink, {
          href: `${getPublicRootUrl()}${Paths.HOME}`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.notFound.docsHome"
          })
        })
      })]
    });
  }
  const errorNode404 = /*#__PURE__*/_jsx(ErrorMessage, {
    type: "404",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "developer-docs-ui.notFound.header"
    }),
    illustrationProps: {
      minBlockSize: '146px'
    },
    children: redirectLink
  });
  const errorNode401 = /*#__PURE__*/_jsx(ErrorMessage, {
    type: "403",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "developer-docs-ui.protected.header"
    }),
    illustrationProps: {
      minBlockSize: '146px'
    },
    children: /*#__PURE__*/_jsx(Link, {
      href: getLoginUrl(),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.protected.cta"
      })
    })
  });
  const errorNode = protectedDoc ? errorNode401 : errorNode404;
  return isPublicDocs ? /*#__PURE__*/_jsx(LayoutPublic, {
    footerWidth: "full",
    maxContentWidth: MAX_WIDTH,
    sideNavComponent: null,
    showFeedbackForm: false,
    children: loading ? /*#__PURE__*/_jsx(LoadingDots, {
      minHeight: 200
    }) : errorNode
  }) : /*#__PURE__*/_jsx(Flex, {
    justifyContent: "center",
    children: errorNode
  });
};
export default NotFound;